import { typeToProductCodeMap } from './campaignNaming';

/**
 * Enhanced Search Utility Functions
 *
 * This utility provides comprehensive search functionality for campaign data,
 * supporting searches across multiple fields including:
 * - Campaign names (partial match)
 * - IO Numbers (exact match for numeric patterns)
 * - Advertiser CP Acronyms (case-insensitive)
 * - Product codes (DIS, EXT, CTV)
 * - Campaign types (mapped from product codes)
 * - Start dates (flexible date format support)
 * - Full naming convention destructuring (IONumber:CPAcronym:name-productCode-startdate YYMMDD)
 */

/**
 * Parse a full naming convention string and extract individual components
 * Format: IONumber:CPAcronym:advertiserName-campaignName-productCode-startdate YYMMDD
 *
 * @param {string} searchTerm - The search term to parse
 * @returns {Object|null} Parsed components or null if not a valid naming convention
 */
export const parseNamingConvention = (searchTerm) => {
  if (!searchTerm || typeof searchTerm !== 'string') return null;

  // Try different naming convention patterns in order of specificity

  // Pattern 1: Full format - 12345:ABC:Advertiser Name-Campaign Name-DIS-231201
  let match = searchTerm.match(/^([A-Za-z0-9]+): ([A-Za-z]+): (.+?)-(.+?)-([A-Za-z]{3})-(\d{6})$/);
  if (match) {
    return {
      ioNumber: match[1],
      cpAcronym: match[2],
      advertiserName: match[3],
      name: match[4],
      productCode: match[5],
      date: match[6],
    };
  }

  // Pattern 2: Without date - 12345:ABC:Advertiser Name-Campaign Name-DIS
  match = searchTerm.match(/^([A-Za-z0-9]+): ([A-Za-z]+): (.+?)-(.+?)-([A-Za-z]{3})$/);
  if (match) {
    return {
      ioNumber: match[1],
      cpAcronym: match[2],
      advertiserName: match[3],
      name: match[4],
      productCode: match[5],
    };
  }

  // Pattern 3: Without product code - 12345:ABC:Advertiser Name-Campaign Name-231201
  match = searchTerm.match(/^([A-Za-z0-9]+): ([A-Za-z]+): (.+?)-(.+?)-(\d{6})$/);
  if (match) {
    return {
      ioNumber: match[1],
      cpAcronym: match[2],
      advertiserName: match[3],
      name: match[4],
      date: match[5],
    };
  }

  // Pattern 4: Just IONumber:CPAcronym:advertiserName-campaignName
  match = searchTerm.match(/^([A-Za-z0-9]+): ([A-Za-z]+): (.+?)-(.+)$/);
  if (match) {
    return {
      ioNumber: match[1],
      cpAcronym: match[2],
      advertiserName: match[3],
      name: match[4],
    };
  }

  // Pattern 4c: IO Number:CP Acronym:Advertiser Name
  match = searchTerm.match(/^([A-Za-z0-9]+): ([A-Za-z]+): (.+)$/);
  if (match) {
    return {
      ioNumber: match[1],
      cpAcronym: match[2],
      advertiserName: match[3],
    };
  }

  // Pattern 4d: IO Number:CP Acronym (specific case)
  match = searchTerm.match(/^([A-Za-z0-9]+): ([A-Za-z]+)$/);
  if (match) {
    return {
      ioNumber: match[1],
      cpAcronym: match[2],
    };
  }

  // Pattern 4e: CP Acronym:Advertiser-Campaign Name
  match = searchTerm.match(/^([A-Za-z]+): (.+?)-(.+)$/);
  if (match) {
    return {
      cpAcronym: match[1],
      advertiserName: match[2],
      name: match[3],
    };
  }

  // Pattern 4f: CP Acronym:Advertiser Name (must come after more specific patterns)
  match = searchTerm.match(/^([A-Za-z]+): (.+)$/);
  if (match) {
    return {
      cpAcronym: match[1],
      advertiserName: match[2],
    };
  }

  // Pattern 5: Missing IO - ABC:Advertiser Name-Campaign Name-DIS-231201
  match = searchTerm.match(/^([A-Za-z]+): (.+?)-(.+?)-([A-Za-z]{3})-(\d{6})$/);
  if (match) {
    return {
      cpAcronym: match[1],
      advertiserName: match[2],
      name: match[3],
      productCode: match[4],
      date: match[5],
    };
  }

  // Pattern 6: Advertiser-Campaign with product code and date - Advertiser Name-Campaign Name-DIS-231201
  match = searchTerm.match(/^(.+?)-(.+?)-([A-Za-z]{3})-(\d{6})$/);
  if (match) {
    return {
      advertiserName: match[1],
      name: match[2],
      productCode: match[3],
      date: match[4],
    };
  }

  // Pattern 7: Advertiser-Campaign with just product code - Advertiser Name-Campaign Name-DIS
  match = searchTerm.match(/^(.+?)-(.+?)-([A-Za-z]{3})$/);
  if (match && ['DIS', 'EXT', 'CTV'].includes(match[3].toUpperCase())) {
    return {
      advertiserName: match[1],
      name: match[2],
      productCode: match[3],
    };
  }

  // Pattern 7b: Name with just product code - Campaign Name-DIS (fallback for old format)
  match = searchTerm.match(/^(.+)-([A-Za-z]{3})$/);
  if (match && ['DIS', 'EXT', 'CTV'].includes(match[2].toUpperCase())) {
    return {
      name: match[1],
      productCode: match[2],
    };
  }

  // Pattern 8: IONumber: (IO Number with trailing colon)
  match = searchTerm.match(/^([A-Za-z0-9]+):$/);
  if (match) {
    return {
      ioNumber: match[1],
    };
  }

  // Pattern 9: :CP Acronym (CP Acronym with leading colon)
  match = searchTerm.match(/^: ([A-Za-z]+)$/);
  if (match) {
    return {
      cpAcronym: match[1],
      advertiserName: match[1],
    };
  }

  // Pattern 10: CP Acronym: (CP Acronym with trailing colon)
  match = searchTerm.match(/^([A-Za-z]+):$/);
  if (match) {
    return {
      cpAcronym: match[1],
    };
  }

  // Pattern 11: :CP Acronym: (CP Acronym with both colons)
  match = searchTerm.match(/^: ([A-Za-z]+):$/);
  if (match) {
    return {
      cpAcronym: match[1],
    };
  }

  // Pattern 12: :Advertiser Name (Advertiser with leading colon)
  match = searchTerm.match(/^:(.+)$/);
  if (match && !/^[A-Za-z]+$/.test(match[1]) && !match[1].includes('-')) {
    return {
      advertiserName: match[1],
    };
  }

  // Pattern 13: :Advertiser Name- (Advertiser with leading colon and trailing dash)
  match = searchTerm.match(/^:(.+?)-$/);
  if (match) {
    return {
      advertiserName: match[1],
    };
  }

  // Pattern 16: -Campaign Name- (Campaign surrounded by hyphens)
  match = searchTerm.match(/^-(.+?)-$/);
  if (match && !/^[A-Za-z]{3}$/.test(match[1]) && !/^\d{6}$/.test(match[1])) {
    return {
      name: match[1],
    };
  }

  // Pattern 15: -Campaign Name (Campaign with leading hyphen)
  match = searchTerm.match(/^-(.+)$/);
  if (
    match &&
    !/^[A-Za-z]{3}$/.test(match[1]) &&
    !/^\d{6}$/.test(match[1]) &&
    !match[1].includes('-')
  ) {
    return {
      name: match[1],
    };
  }

  // Pattern 14: Advertiser Name- (Advertiser with trailing hyphen)
  match = searchTerm.match(/^(.+?)-$/);
  if (match && !/^[A-Za-z]{3}$/.test(match[1]) && !/^\d{6}$/.test(match[1])) {
    return {
      advertiserName: match[1],
      name: match[1],
    };
  }

  // Pattern 18: -Product Code (Product code with leading hyphen)
  match = searchTerm.match(/^-([A-Za-z]{3})$/);
  if (match && ['DIS', 'EXT', 'CTV'].includes(match[1].toUpperCase())) {
    return {
      productCode: match[1],
    };
  }

  // Pattern 19: -Product Code- (Product code surrounded by hyphens)
  match = searchTerm.match(/^-([A-Za-z]{3})-$/);
  if (match && ['DIS', 'EXT', 'CTV'].includes(match[1].toUpperCase())) {
    return {
      productCode: match[1],
    };
  }

  // Pattern 20: Product Code- (Product code with trailing hyphen)
  match = searchTerm.match(/^([A-Za-z]{3})-$/);
  if (match && ['DIS', 'EXT', 'CTV'].includes(match[1].toUpperCase())) {
    return {
      productCode: match[1],
    };
  }

  // Pattern 21: -Start Date (Date with leading hyphen)
  match = searchTerm.match(/^-(\d{6})$/);
  if (match) {
    return {
      date: match[1],
    };
  }

  return null;
};

/**
 * Check if a search term looks like a full naming convention string
 * @param {string} searchTerm - The search term to evaluate
 * @returns {boolean} True if it looks like a naming convention string
 */
export const looksLikeNamingConvention = (searchTerm) => {
  if (!searchTerm || typeof searchTerm !== 'string') return false;

  // Look for patterns that suggest naming convention:
  // - Contains colons and hyphens
  // - Has multiple segments separated by colons or hyphens
  // - Contains date-like patterns at the end
  // - Contains product codes (DIS, EXT, CTV)

  const indicators = [
    /[A-Za-z0-9]+:[A-Za-z]+:/, // IONumber:CPAcronym: pattern
    /-[A-Za-z]{3}-\d{6}$/, // -productCode-YYMMDD at end
    /-[A-Za-z]{3}$/, // -productCode at end
    /:[^:]+:[^:]+/, // Multiple colon-separated segments
    /-(?:DIS|EXT|CTV)-/, // Specific product codes
    /-(?:DIS|EXT|CTV)$/, // Product codes at end
    /:.+-.*-./, // Pattern suggesting advertiser-campaign structure
    /[A-Za-z0-9]+:[A-Za-z]+:.+-.*/, // IONumber:CPAcronym:advertiser-campaign pattern
    /^[A-Za-z0-9]+:[A-Za-z]+:.+$/, // IO Number:CP Acronym:Advertiser Name pattern
    /^[A-Za-z0-9]+:[A-Za-z]+$/, // IO Number:CP Acronym pattern
    /^[A-Za-z]+:.+-.*/, // CP Acronym:Advertiser-Campaign Name pattern
    /^[A-Za-z]+:.+/, // CP Acronym:Advertiser Name pattern
    /^[A-Za-z0-9]+:$/, // IONumber: pattern
    /^:[A-Za-z]+$/, // :CP Acronym pattern
    /^[A-Za-z]+:$/, // CP Acronym: pattern
    /^:[A-Za-z]+:$/, // :CP Acronym: pattern
    /^:.+$/, // :Advertiser Name pattern
    /^:.+-$/, // :Advertiser Name- pattern
    /^.* .*-$/, // Advertiser Name- pattern (contains space)
    /^.+-$/, // General Name- pattern (advertiser or campaign)
    /^-.+$/, // -Campaign Name pattern
    /^-.+-$/, // -Campaign Name- pattern
    /^-(?:DIS|EXT|CTV)$/, // -Product Code pattern
    /^-(?:DIS|EXT|CTV)-$/, // -Product Code- pattern
    /^(?:DIS|EXT|CTV)-$/, // Product Code- pattern
    /^-\d{6}$/, // -Start Date pattern
    /(?::[A-Za-z0-9]+|[A-Za-z0-9]+:|:[A-Za-z0-9]+:)/, // :any string: pattern
  ];

  return indicators.some((pattern) => pattern.test(searchTerm));
};

/**
 * Create search filters from parsed naming convention components
 * @param {Object} parsedComponents - Components from parseNamingConvention
 * @returns {Object[]} Array of search filter objects
 */
export const createFiltersFromComponents = (parsedComponents) => {
  const filters = [];

  if (!parsedComponents) return filters;

  // IO Number filter
  if (parsedComponents.ioNumber) {
    filters.push({ IONumber_contains: parsedComponents.ioNumber });
  }

  // CP Acronym filter - search in advertiser CPAcronym and Platform/Channel Partner parent orgs
  if (parsedComponents.cpAcronym) {
    filters.push({
      OR: [
        { advertiser: { CPAcronym: parsedComponents.cpAcronym } },
        { advertiser: { parentOrg_some: { CPAcronym: parsedComponents.cpAcronym } } },
      ],
    });
  }

  // Advertiser name filter
  if (parsedComponents.advertiserName) {
    filters.push({
      OR: [{ advertiser: { name_contains: parsedComponents.advertiserName } }],
    });
  }

  // Campaign name filter
  if (parsedComponents.name) {
    filters.push({ name_contains: parsedComponents.name });
  }

  // Product code filter
  if (parsedComponents.productCode) {
    filters.push({ productCode_contains: parsedComponents.productCode });

    // Also search by type if product code maps to a type
    const matchingTypes = getTypesFromProductCode(parsedComponents.productCode);
    if (matchingTypes.length > 0) {
      filters.push({ type_in: matchingTypes });
    }
  }

  // Date filter
  // Check if the number of digits is 6 (YYMMDD)
  if (parsedComponents.date && /^\d{6}$/.test(parsedComponents.date)) {
    const year = `20${parsedComponents.date.substring(0, 2)}`; // YYYY
    const month = parsedComponents.date.substring(2, 4); // MM
    const day = parsedComponents.date.substring(4, 6); // DD

    if (month >= '01' && month <= '12' && day >= '01' && day <= '31') {
      filters.push({
        startDate_gte: `${year}-${month}-${day}T00:00:00.000Z`,
        startDate_lte: `${year}-${month}-${day}T23:59:59.999Z`,
      });
    }
  }

  return filters;
};

/**
 * Get campaign types that match a product code search term
 * @param {string} search - The search term to match against product codes
 * @returns {string[]} Array of matching campaign types
 */
export const getTypesFromProductCode = (search) => {
  const upperSearch = search.toUpperCase();
  return Object.entries(typeToProductCodeMap)
    .filter(([type, code]) => code.includes(upperSearch))
    .map(([type]) => type);
};

/**
 * Check if a search term looks like a date pattern (2-6 digits)
 * @param {string} search - The search term to evaluate
 * @returns {boolean} True if the search term matches date patterns
 */
export const isDateLikeSearch = (search) => {
  return /^\d{2,6}$/.test(search); // Matches 2-6 digits for date patterns
};

/**
 * Generate date-based search filters for various date formats
 * Supports:
 * - YY (year): 23 -> searches 2023-01-01 to 2023-12-31
 * - YYMM (year-month): 2312 -> searches 2023-12-01 to 2023-12-31
 * - YYMMDD (full date): 231201 -> searches exact date 2023-12-01
 *
 * @param {string} search - The date search term
 * @returns {Object[]} Array of date filter objects for GraphQL queries
 */
export const getDateSearchFilters = (search) => {
  if (search.length === 6) {
    // Full date search (YYMMDD)
    const year = `20${search.substring(0, 2)}`;
    const month = search.substring(2, 4);
    const day = search.substring(4, 6);
    if (month >= '01' && month <= '12' && day >= '01' && day <= '31') {
      return {
        startDate_gte: `${year}-${month}-${day}T00:00:00.000Z`,
        startDate_lte: `${year}-${month}-${day}T23:59:59.999Z`,
      };
    }
  }

  if (search.length >= 4) {
    // Year-Month search (YYMM)
    const year = `20${search.substring(0, 2)}`;
    const month = search.substring(2, 4);
    if (month >= '01' && month <= '12') {
      const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
      return {
        startDate_gte: `${year}-${month}-01T00:00:00.000Z`,
        startDate_lte: `${year}-${month}-${daysInMonth}T23:59:59.999Z`,
      };
    }
  }

  if (search.length >= 2) {
    // Year search (YY) - assumes 20XX format
    const year = `20${search.substring(0, 2)}`;
    return {
      startDate_gte: `${year}-01-01T00:00:00.000Z`,
      startDate_lte: `${year}-12-31T23:59:59.999Z`,
    };
  }
};

/**
 * Create a comprehensive search filter for campaign data
 * This function intelligently detects search patterns and creates appropriate
 * GraphQL filters for all relevant campaign fields.
 *
 * @param {string} search - The search term entered by the user
 * @returns {Object|null} GraphQL filter object with OR conditions, or null if no search term
 */
export const createEnhancedSearchFilter = (search) => {
  if (!search || !search.trim()) return null;

  const searchTerm = search.trim();
  let searchFilters = [];
  // STEP 1: Try to destructure as full naming convention first
  if (looksLikeNamingConvention(searchTerm)) {
    const parsedComponents = parseNamingConvention(searchTerm);
    if (parsedComponents) {
      // Successfully parsed naming convention - use component-based search
      const componentFilters = createFiltersFromComponents(parsedComponents);

      if (componentFilters.length > 0) {
        // Use AND logic for component-based search (more precise)
        const values = Object.values(parsedComponents);
        let searchFilter = { OR: [{ AND: componentFilters }] };
        if (values.every((v) => v === values[0])) {
          // If all components are the same, use OR instead since the goal is the search the same keyword to all fields
          searchFilter = { OR: [{ OR: componentFilters }] };
        }

        searchFilter.OR.push({ name_contains: searchTerm });
        return searchFilter;
      }
    }
  }

  // STEP 2: If not a naming convention or couldn't parse, fall back to general search

  // Always include basic name search
  searchFilters.push({ name_contains: searchTerm });

  // Search in IO Number (for numeric patterns)
  if (/^\d+$/.test(searchTerm)) {
    searchFilters.push({ IONumber_contains: searchTerm });
  }

  searchFilters.push({
    OR: [
      { advertiser: { name_contains: searchTerm } },
      { advertiser: { parentOrg_some: { name_contains: searchTerm } } },
    ],
  });

  // Search in advertiser CP Acronym and advertiser name (for alphabetic patterns)
  if (/^[A-Za-z]+$/.test(searchTerm)) {
    searchFilters.push({
      OR: [
        { advertiser: { CPAcronym_contains: searchTerm } },
        { advertiser: { parentOrg_some: { CPAcronym_contains: searchTerm } } },
        { advertiser: { name_contains: searchTerm } },
        { advertiser: { parentOrg_some: { name_contains: searchTerm } } },
      ],
    });
  }

  // Search in product code
  searchFilters.push({ productCode_contains: searchTerm });

  // Search by type if product code matches
  const matchingTypes = getTypesFromProductCode(searchTerm);
  if (matchingTypes.length > 0) {
    searchFilters.push({ type_in: matchingTypes });
  }

  // Search by date patterns if search looks like a date
  if (isDateLikeSearch(searchTerm)) {
    searchFilters.push(getDateSearchFilters(searchTerm));
  }

  return { OR: searchFilters };
};

/**
 * Get search capabilities description for UI tooltips
 * @returns {Object} Object containing search capability descriptions
 */
export const getSearchCapabilities = () => {
  return {
    title: 'Enhanced Search Features:',
    features: [
      'Campaign names (partial match)',
      'IO Numbers (exact match)',
      'Advertiser acronyms (e.g., "ABC")',
      'Advertiser names (partial match)',
      'Product codes (DIS, EXT, CTV)',
      'Dates (YYMMDD format, e.g., "231201")',
      'Partial dates (YY or YYMM)',
      'Full naming convention (e.g., "12345:ABC:Advertiser-Campaign-DIS-231201")',
      'Auto-destructure naming convention components',
      'Search in Platform/Channel Partner hierarchy',
    ],
  };
};

/**
 * Validate search term and provide feedback
 * @param {string} search - The search term to validate
 * @returns {Object} Validation result with isValid flag and suggestions
 */
export const validateSearchTerm = (search) => {
  if (!search || search.trim().length === 0) {
    return {
      isValid: false,
      message: 'Please enter a search term',
    };
  }

  const trimmedSearch = search.trim();

  // Check for potentially problematic patterns
  if (trimmedSearch.length === 1) {
    return {
      isValid: true,
      message: 'Single character searches may return many results',
    };
  }

  if (isDateLikeSearch(trimmedSearch) && trimmedSearch.length < 2) {
    return {
      isValid: false,
      message: 'Date searches require at least 2 digits (YY format)',
    };
  }

  return {
    isValid: true,
    message: 'Search term is valid',
  };
};

/**
 * Get search suggestions based on the current search term
 * @param {string} search - Current search term
 * @returns {string[]} Array of search suggestions
 */
export const getSearchSuggestions = (search) => {
  if (!search || search.trim().length === 0) {
    return [
      'Try searching by campaign name',
      'Enter IO number (e.g., 12345)',
      'Search by advertiser acronym',
      'Use product codes: DIS, EXT, CTV',
      'Search by date: YYMMDD format',
    ];
  }

  const trimmedSearch = search.trim();
  const suggestions = [];

  // Numeric suggestions
  if (/^\d+$/.test(trimmedSearch)) {
    suggestions.push('Searching in IO Numbers and dates');
    if (trimmedSearch.length < 6) {
      suggestions.push('Add more digits for specific date search');
    }
  }

  // Alphabetic suggestions
  if (/^[A-Za-z]+$/.test(trimmedSearch)) {
    suggestions.push(
      'Searching in campaign names, advertiser names, and advertiser acronyms (including Platform/Channel Partner hierarchy)'
    );
    if (
      Object.values(typeToProductCodeMap).some((code) =>
        code.toLowerCase().includes(trimmedSearch.toLowerCase())
      )
    ) {
      suggestions.push('Matches product code pattern');
    }
  }

  // Date suggestions
  if (isDateLikeSearch(trimmedSearch)) {
    if (trimmedSearch.length === 2) {
      suggestions.push('Year search - add month (YYMM) for more specific results');
    } else if (trimmedSearch.length === 4) {
      suggestions.push('Year-Month search - add day (YYMMDD) for exact date');
    } else if (trimmedSearch.length === 6) {
      suggestions.push('Exact date search');
    }
  }

  return suggestions.length > 0 ? suggestions : ['Continue typing for more specific results'];
};
